import React from 'react';
import { Controller, type Control } from 'react-hook-form';
import { Pressable } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { avatarDefault } from '@/assets/images';
import { Divider, Stack } from '@/components/layout';
import { Checkbox, FormInput, Image, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import RelationshipSelector, {
  type RelationshipOption,
} from '@/features/guardian-player/components/invite-guardian/relationship-selector';
import type { RemoveCoGuardianForm } from '@/features/guardian-player/schemas/remove-co-guardian-schema';
import { MAX_REMOVE_DETAIL_LENGTH } from '@/features/guardian-player/schemas/remove-co-guardian-schema';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

const strings = STRINGS.guardianPlayer.removeCoGuardian;

const CO_AVATAR_SIZE = 40;
const KID_AVATAR_SIZE = 60;
const TABLET_WIDTH = '38%'; // Follow design width 380 / 1024 ~ 38%

export type RemoveCoGuardianContentProps = {
  screenTitle: string;
  /** Co-guardian being removed. */
  coGuardianFirstName: string;
  coGuardianFullName: string;
  coGuardianAvatarUrl?: string;
  coGuardianRoleLabel: string;
  /** "Joined since Sep 2023", omitted when unknown. */
  coGuardianJoinedLabel?: string;
  /** The player whose circle the co-guardian is being removed from. */
  kidFirstName: string;
  kidFullName: string;
  kidAvatarUrl?: string;
  /** "Age 8 · Level 3" or "Level 3". */
  kidDetailLine: string;
  /** "Since Sep 2023", omitted when unknown. */
  kidSinceLine?: string;
  reasonOptions: RelationshipOption[];
  control: Control<RemoveCoGuardianForm>;
  /** True once a reason is chosen and the acknowledgement box is ticked. */
  canSubmit: boolean;
  onBack: () => void;
  onRemove: () => void;
  removing?: boolean;
};

export default function RemoveCoGuardianContent({
  screenTitle,
  coGuardianFirstName,
  coGuardianFullName,
  coGuardianAvatarUrl,
  coGuardianRoleLabel,
  coGuardianJoinedLabel,
  kidFirstName,
  kidFullName,
  kidAvatarUrl,
  kidDetailLine,
  kidSinceLine,
  reasonOptions,
  control,
  canSubmit,
  onBack,
  onRemove,
  removing = false,
}: RemoveCoGuardianContentProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const { bottom } = useSafeAreaInsets();

  const coInitial = coGuardianFullName.trim().charAt(0).toUpperCase();
  const removeDisabled = !canSubmit || removing;

  const effectItems = [
    strings.effectLosesAccess
      .replace('{name}', coGuardianFirstName)
      .replace('{player}', kidFirstName),
    strings.effectBookingsStay.replace('{name}', coGuardianFirstName),
  ];

  return (
    <Stack
      viewType="gradient"
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.91]}
      start={{ x: 1, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={styles.root}>
      <GuardianScreenHeader
        title={screenTitle}
        onBack={onBack}
        titleAlign="center"
        titleVariant="Font Guardian App/Body/Body 16 Bold"
        containerProps={!isTablet ? { backgroundColor: 'transparent' } : undefined}
      />

      <KeyboardAwareScrollView
        style={styles.keyboardAvoidingView}
        contentContainerStyle={[
          styles.content,
          { paddingBottom: Math.max(bottom + scale(12), scale(24)) },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bottomOffset={scale(12)}>
        <Stack
          flex={1}
          width="100%"
          maxWidth={isTablet ? TABLET_WIDTH : undefined}
          alignSelf="center"
          gap={scale(20)}>
          {/* Co-guardian card */}
          <Stack style={styles.card} direction="row" align="center" gap={scale(10)}>
            <Stack
              align="center"
              justify="center"
              width={scale(CO_AVATAR_SIZE)}
              height={scale(CO_AVATAR_SIZE)}
              borderRadius={scale(CO_AVATAR_SIZE / 2)}
              backgroundColor={guardianColors['8']}
              overflow="hidden">
              {coGuardianAvatarUrl ? (
                <Image
                  source={{ uri: coGuardianAvatarUrl }}
                  width={scale(CO_AVATAR_SIZE)}
                  height={scale(CO_AVATAR_SIZE)}
                  circular
                  resizeMode="cover"
                />
              ) : (
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                  style={styles.coInitial}>
                  {coInitial}
                </Text>
              )}
            </Stack>
            <Stack flex={1}>
              <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.name}>
                {coGuardianFullName}
              </Text>
              <Stack direction="row" align="center" gap={scale(6)}>
                <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.role}>
                  {coGuardianRoleLabel}
                </Text>
                {coGuardianJoinedLabel ? (
                  <>
                    <Stack style={styles.dot} />
                    <Text
                      variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                      style={styles.since}>
                      {coGuardianJoinedLabel}
                    </Text>
                  </>
                ) : null}
              </Stack>
            </Stack>
          </Stack>

          {/* Kid + effect card */}
          <Stack style={styles.card} gap={scale(10)}>
            <Stack direction="row" align="center" gap={scale(16)}>
              <Image
                source={kidAvatarUrl ? { uri: kidAvatarUrl } : avatarDefault}
                width={scale(KID_AVATAR_SIZE)}
                height={scale(KID_AVATAR_SIZE)}
                circular
                resizeMode="cover"
                defaultSource={avatarDefault}
              />
              <Stack flex={1}>
                <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.name}>
                  {kidFullName}
                </Text>
                <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.role}>
                  {kidDetailLine}
                </Text>
                {kidSinceLine ? (
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                    style={styles.since}>
                    {kidSinceLine}
                  </Text>
                ) : null}
              </Stack>
            </Stack>

            <Divider color={guardianColors['4']} />

            <Stack direction="row" align="center" gap={scale(6)}>
              <SvgIcon name="IcListCheck" size={scale(16)} color={guardianColors['Main/Main']} />
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 11 Semibold"
                style={styles.sectionTitle}>
                {strings.effectTitle}
              </Text>
            </Stack>

            <Stack gap={scale(8)}>
              {effectItems.map(item => (
                <Stack key={item} direction="row" align="center" gap={scale(8)}>
                  <SvgIcon name="IcMinusCircle" size={scale(24)} color={guardianColors['6']} />
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                    style={styles.effectItem}>
                    {item}
                  </Text>
                </Stack>
              ))}
            </Stack>
          </Stack>

          {/* Reason */}
          <Controller
            control={control}
            name="reason"
            render={({ field: { value, onChange } }) => (
              <Stack gap={scale(12)}>
                <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.label}>
                  {strings.reasonLabel}
                  <Text style={styles.requiredMark}> *</Text>
                </Text>
                <RelationshipSelector
                  options={reasonOptions}
                  value={value || null}
                  onChange={onChange}
                />
              </Stack>
            )}
          />

          {/* Detail */}
          <Controller
            control={control}
            name="detail"
            render={({ field: { value, onChange, onBlur } }) => (
              <FormInput
                label={strings.detailPlaceholder.replace('{name}', coGuardianFirstName)}
                hideLabel
                inputVariant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                inputStyle={styles.detailText}
                contentStyle={styles.detailContent}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                placeholder={strings.detailPlaceholder.replace('{name}', coGuardianFirstName)}
                multiline
                numberOfLines={4}
                maxLength={MAX_REMOVE_DETAIL_LENGTH}
              />
            )}
          />

          {/* Acknowledgement */}
          <Controller
            control={control}
            name="acknowledged"
            render={({ field: { value, onChange } }) => (
              <Checkbox checked={value} onValueChange={onChange}>
                <Text variant="Inter/Paragraph/X Small" style={styles.acknowledgeLabel}>
                  {strings.acknowledgement.replace('{name}', coGuardianFirstName)}
                </Text>
              </Checkbox>
            )}
          />

          {/* Sensitive-action warning */}
          <Stack direction="row" align="flex-start" gap={scale(8)} style={styles.warning}>
            <SvgIcon name="IcWarning" size={scale(20)} color={guardianColors.Error} />
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
              style={styles.warningText}>
              {strings.warning.replace(/{name}/g, coGuardianFirstName)}
            </Text>
          </Stack>

          {!isTablet && <Stack flex={1} minHeight={scale(20)} />}

          {/* Remove */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={strings.remove.replace('{name}', coGuardianFirstName)}
            accessibilityState={{ disabled: removeDisabled }}
            disabled={removeDisabled}
            onPress={onRemove}
            style={({ pressed }) => [
              styles.removeButton,
              removeDisabled ? styles.removeDisabled : null,
              pressed && !removeDisabled ? styles.pressed : null,
            ]}>
            <SvgIcon name="IcLeave" size={scale(20)} color={colors['Level 3']} />
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
              style={styles.removeLabel}>
              {strings.remove.replace('{name}', coGuardianFirstName)}
            </Text>
          </Pressable>
        </Stack>
      </KeyboardAwareScrollView>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
    width: '100%',
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: scale(16),
    paddingTop: scale({ phone: 4, tablet: 16 }),
  },
  card: {
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    borderCurve: 'continuous',
    padding: scale(12),
  },
  coInitial: {
    color: guardianColors.Blue,
  },
  name: {
    color: guardianColors['10'],
  },
  role: {
    color: guardianColors['Main/Main'],
  },
  since: {
    color: guardianColors['6'],
  },
  dot: {
    width: scale(2),
    height: scale(2),
    borderRadius: scale(1),
    backgroundColor: guardianColors['6'],
  },
  sectionTitle: {
    color: guardianColors['6'],
  },
  effectItem: {
    flex: 1,
    color: guardianColors['10'],
  },
  label: {
    color: guardianColors['10'],
  },
  requiredMark: {
    color: guardianColors.Error,
  },
  detailContent: {
    borderColor: guardianColors['3'],
    borderRadius: scale(8),
    height: scale(100),
    alignItems: 'flex-start',
    paddingLeft: scale(12),
    paddingRight: scale(12),
    paddingVertical: scale(8),
  },
  detailText: {
    color: guardianColors['10'],
    marginLeft: 0,
    height: '100%',
    textAlignVertical: 'top',
  },
  acknowledgeLabel: {
    flex: 1,
    color: guardianColors['10'],
  },
  warning: {
    borderRadius: scale(12),
    borderCurve: 'continuous',
    paddingHorizontal: scale(8),
    paddingVertical: scale(12),
    borderWidth: 0.5,
    borderColor: colors['Level 3'],
    backgroundColor: withOpacity('#FF2D2D', 0.08),
  },
  warningText: {
    flex: 1,
    color: guardianColors['10'],
  },
  removeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(8),
    height: scale(48),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    borderWidth: 1,
    borderColor: colors['Level 3'],
    backgroundColor: colors['0'],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  removeDisabled: {
    opacity: 0.5,
  },
  removeLabel: {
    color: colors['Level 3'],
  },
  pressed: {
    opacity: 0.7,
  },
}));
